import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronsLeft,
    faChevronsRight,
} from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import * as d3 from "d3";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    type InfrastructureSitesListEmissionStatsParameter,
    InfrastructureSitesListEmissionStatsParameterToJSON,
    type Site,
} from "../../../../../apiClient/generated";
import { useInfrastructureApiClient } from "../../../../../hooks";
import { useResizeObserver } from "../../../../../ui/Plot/hooks";
import { useDrawAndFilterControlsV2 } from "../../../hooks/areaFilter";
import { useMapData, useMapFilters } from "../../../hooks/mapDataAndFilters";
import { useMap } from "../../../hooks/mapState";
import { automaticPanAndZoomAtom, prevMapViewStateAtom } from "../../../state";
import { InsightsContainer } from "../../../ui/MapControlsV2";
import { SettingsDropdownProvider } from "./ui";

const LoadingOverlay = () => (
    <div className="absolute h-full w-full text-ae-slate-600 bg-white bg-opacity-60 flex items-center justify-center">
        <FontAwesomeIcon icon={faLoader} className="h-4 w-4 animate-spin" />
    </div>
);

interface CircleGraphProps {
    value: number;
    total: number;
    label: string;
}

const CircleGraph = (props: CircleGraphProps) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const dimensions = useResizeObserver(containerRef);

    useEffect(() => {
        if (dimensions.width === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const size = 60;
        const radius = size / 2 - 5;
        const strokeWidth = 6;
        const percentage =
            props.total > 0 ? (props.value / props.total) * 100 : 0;

        const colorScale = d3
            .scaleLinear()
            .domain([0, 50, 100])
            .range(["#ef4444", "#fb923c", "#4ade80"] as any[]);

        const strokeColor = colorScale(percentage);

        const g = svg
            .attr("width", size)
            .attr("height", size)
            .append("g")
            .attr("transform", `translate(${size / 2}, ${size / 2})`);

        // Background circle
        g.append("circle")
            .attr("r", radius)
            .attr("fill", "none")
            .attr("stroke", "#e2e8f0")
            .attr("stroke-width", strokeWidth);

        // Progress circle
        const circumference = 2 * Math.PI * radius;
        const progressOffset =
            circumference - (percentage / 100) * circumference;

        g.append("circle")
            .attr("r", radius)
            .attr("fill", "none")
            .attr("stroke", strokeColor)
            .attr("stroke-width", strokeWidth)
            .attr("stroke-linecap", "round")
            .attr("stroke-dasharray", circumference)
            .attr("stroke-dashoffset", circumference)
            .attr("transform", "rotate(-90)")
            .attr("stroke-dashoffset", progressOffset);

        // Percentage text
        g.append("text")
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .style("font-size", `${size * 0.25}px`)
            .style("font-weight", "600")
            .style("fill", "#2d3748")
            .text(`${Math.round(percentage)}%`);
    }, [props, dimensions]);

    return (
        <div
            ref={containerRef}
            className="flex flex-col items-center justify-center"
        >
            <svg ref={svgRef} />
            <div className="text-sm text-center">{props.label}</div>
        </div>
    );
};

const InfrastructureHealth = () => {
    const apiClient = useInfrastructureApiClient();
    const { debounced } = useMap("mainMap");
    const { filterByArea, areaToFetch } = useDrawAndFilterControlsV2("mainMap");

    const infraHealthQuery = useQuery({
        queryKey: ["infrastructureHealth", areaToFetch, filterByArea],
        queryFn: async () => {
            return await apiClient.infrastructureSitesInfrastructureStatsRetrieve(
                {
                    locationWithin: areaToFetch,
                    locationPreset:
                        filterByArea.selectedFilters &&
                        filterByArea.selectedFilters.length > 0
                            ? filterByArea.selectedFilters
                            : undefined,
                },
            );
        },
        enabled: !!debounced.areaOnScreen,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        staleTime: 500,
    });

    return (
        <InsightsContainer className="w-1/2 h-full overflow-hidden">
            <p className="px-1 font-bold text-sm">Infrastructure Health</p>

            <div className="flex items-center justify-evenly my-5 relative">
                {infraHealthQuery.isFetching && <LoadingOverlay />}
                <CircleGraph
                    label="Polygons"
                    value={infraHealthQuery.data?.siteWithPolygons || 0}
                    total={infraHealthQuery.data?.siteCount || 0}
                />
                <CircleGraph
                    label="Equipment"
                    value={infraHealthQuery.data?.siteWithEquipment || 0}
                    total={infraHealthQuery.data?.siteCount || 0}
                />
            </div>

            <div className="w-full items-center justify-center text-xs text-center">
                Sites last updated{" "}
                {Math.abs(
                    DateTime.fromJSDate(
                        infraHealthQuery.data?.lastUpdatedAt,
                    ).diffNow("days").days,
                ).toFixed(0)}{" "}
                days ago.
            </div>
        </InsightsContainer>
    );
};

const insightsEmissionAboveThresholdAtom = atomWithStorage(
    "insightsEmissionAboveThreshold",
    100,
);

const SitesOfInterest = () => {
    const apiClient = useInfrastructureApiClient();
    const [mode, setMode] = useState<"COUNT" | "SUM">("COUNT");
    const [aboveNumber, setAboveNumber] = useAtom(
        insightsEmissionAboveThresholdAtom,
    );
    const PAGE_SIZE = 5;
    const [page, setPage] = useState(1);
    const { debounced, flyTo } = useMap("mainMap");
    const { filterState } = useMapFilters("mainMap");
    const { filterByArea, areaToFetch } = useDrawAndFilterControlsV2("mainMap");
    const [autoMove] = useAtom(automaticPanAndZoomAtom);
    const [, setPrevMapViewState] = useAtom(prevMapViewStateAtom);
    const { selectedContext, setSelectedContext } = useMapData("mainMap");

    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore =
                filterState.emissions.endDateFilter !== ""
                    ? new Date(filterState.emissions.endDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.end) {
                dateBefore = new Date(selectedContext.dateRangeToShowData.end);
            }
            let dateAfter =
                filterState.emissions.startDateFilter !== ""
                    ? new Date(filterState.emissions.startDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.start) {
                dateAfter = new Date(selectedContext.dateRangeToShowData.start);
            }
            return { dateBefore, dateAfter };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            selectedContext.dateRangeToShowData,
        ]),
        400,
    );

    // Reset to first page when any filter changes
    useEffect(() => {
        setPage(1);
    }, [
        debounced.areaOnScreen,
        filterByArea.selectedFilters,
        filterState.emissions.filterEmissionsBy,
        debouncedDateRange,
        mode,
        aboveNumber,
    ]);

    const sitesWithMetricsQuery = useQuery({
        queryKey: [
            "infrastructureHealth",
            debounced.areaOnScreen,
            filterByArea.selectedFilters,
            filterState.emissions.filterEmissionsBy,
            debouncedDateRange,
            filterByArea,
            mode,
            aboveNumber,
            page,
        ],
        queryFn: async () => {
            return await apiClient.infrastructureSitesList({
                page,
                pageSize: PAGE_SIZE,
                ordering:
                    mode === "COUNT" ? "-emission_count" : "-emission_sum",
                emissionStats: JSON.stringify(
                    InfrastructureSitesListEmissionStatsParameterToJSON({
                        annotate: true,
                        emissionThreshold: aboveNumber * 1000,
                        detectionStartDate:
                            filterState.emissions.filterEmissionsBy ===
                            "detection_date"
                                ? debouncedDateRange.dateAfter
                                : undefined,
                        detectionEndDate:
                            filterState.emissions.filterEmissionsBy ===
                            "detection_date"
                                ? debouncedDateRange.dateBefore
                                : undefined,
                        matchEndDate:
                            filterState.emissions.filterEmissionsBy ===
                            "match_date"
                                ? debouncedDateRange.dateAfter
                                : undefined,
                        matchStartDate:
                            filterState.emissions.filterEmissionsBy ===
                            "match_date"
                                ? debouncedDateRange.dateBefore
                                : undefined,
                    }),
                ) as unknown as InfrastructureSitesListEmissionStatsParameter,
                locationWithin: areaToFetch,
                locationPreset:
                    filterByArea.selectedFilters &&
                    filterByArea.selectedFilters.length > 0
                        ? filterByArea.selectedFilters
                        : undefined,
            });
        },
        enabled: !!debounced.areaOnScreen,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        staleTime: 500,
    });

    const totalPages = sitesWithMetricsQuery.data?.count
        ? Math.ceil(sitesWithMetricsQuery.data.count / PAGE_SIZE)
        : 1;

    const goToSite = (site: Site) => {
        if (autoMove) {
            setPrevMapViewState(debounced.viewState);
            flyTo(
                site.location.coordinates[1],
                site.location.coordinates[0],
                15,
                true,
            );
        }
        setSelectedContext({ siteId: site.id });
    };

    return (
        <InsightsContainer className="w-1/2 h-full">
            <div className="w-full flex items-center justify-between">
                <p className="font-bold text-sm px-1">Sites of interest</p>
                {totalPages > 1 && (
                    <div className="flex items-center gap-1 px-1">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="text-ae-blue-550 disabled:opacity-30 hover:text-ae-slate-900"
                        >
                            <FontAwesomeIcon
                                icon={faChevronsLeft}
                                className="h-3 w-3"
                            />
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                setPage((p) => Math.min(totalPages, p + 1))
                            }
                            disabled={page === totalPages}
                            className="text-ae-blue-550 disabled:opacity-30 hover:text-ae-slate-900"
                        >
                            <FontAwesomeIcon
                                icon={faChevronsRight}
                                className="h-3 w-3"
                            />
                        </button>
                    </div>
                )}
                <SettingsDropdownProvider>
                    <div className="px-1">
                        <div className="text-xs text-ae-slate-600">
                            Display:
                        </div>
                        <label className="flex items-center text-sm space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="modeState"
                                checked={mode === "COUNT"}
                                onChange={() => setMode("COUNT")}
                                className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">
                                Count of emissions
                            </span>
                        </label>
                        <label className="flex items-center text-sm space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="modeState"
                                checked={mode === "SUM"}
                                onChange={() => setMode("SUM")}
                                className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">
                                Total rate (in kg/h)
                            </span>
                        </label>
                        <hr className="my-1 bg-ae-slate-600" />
                        <div>
                            <div className="text-xs text-ae-slate-600">
                                Emission Threshold (kg/h)*:
                            </div>
                            <input
                                type="number"
                                className="py-0.5 w-40 px-1 text-sm rounded border border-ae-slate-300"
                                value={aboveNumber}
                                onChange={(e) =>
                                    setAboveNumber(Number(e.target.value))
                                }
                            />
                            <div className="text-xs text-ae-slate-600 mt-2 max-w-40">
                                *: Only emissions above selected thresold will
                                be counted in the sum
                            </div>
                        </div>
                    </div>
                </SettingsDropdownProvider>
            </div>
            <div className="w-full overflow-hidden whitespace-nowrap relative">
                {sitesWithMetricsQuery.isFetching && <LoadingOverlay />}
                <table className="w-full text-sm border-collapse table-fixed">
                    <thead>
                        <tr className="text-xs text-gray-500 text-right border-b">
                            <th className="text-left font-normal py-0.5 w-4">
                                #
                            </th>
                            <th className="text-left w-auto">Site Name</th>
                            <th
                                className={`py-0.5 text-right ${mode === "SUM" ? "w-20" : "w-10"}`}
                            >
                                {mode === "COUNT" ? "Count" : "Rate"}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sitesWithMetricsQuery.data?.results.map(
                            (site, index) => (
                                <tr
                                    key={site.id}
                                    onClick={() => goToSite(site)}
                                    className="hover:bg-gray-50 text-ae-slate-600 cursor-pointer border-b last:border-0"
                                >
                                    <td className="text-xs text-left px-1 py-0.5">
                                        {(page - 1) * PAGE_SIZE + index + 1}.
                                    </td>
                                    <td
                                        className="px-1 py-0.5"
                                        title={site.siteName}
                                    >
                                        <div className="truncate">
                                            {site.siteName}
                                        </div>
                                    </td>
                                    <td className="px-1 py-0.5 text-right">
                                        {mode === "COUNT" ? (
                                            <span className="text-gray-800">
                                                {site.emissionCount}
                                            </span>
                                        ) : (
                                            <div>
                                                <span className="text-gray-800">
                                                    {(
                                                        site.emissionSum / 1000
                                                    ).toFixed(0)}
                                                </span>
                                                <span className="ml-1 text-xs text-gray-500">
                                                    kg/h
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>
        </InsightsContainer>
    );
};

export const InfrastuctureInsightsRow = () => {
    const { filterState } = useMapFilters("mainMap");

    if (!filterState.infrastructure.infraTypeFilter.includes("SITE")) {
        return null;
    }

    return (
        <div className="flex items-center w-full gap-2 h-44">
            <InfrastructureHealth />
            <SitesOfInterest />
        </div>
    );
};
